import { Link } from "@tanstack/react-router";
import { CircleHelp, Menu as MenuIcon } from "lucide-react";
import { IconButton, Menu, Tooltip, cn } from "@platform/ui";
import { useConsole } from "../context";
import { setLocale, useT } from "../i18n";
import { initials } from "../lib/format";
import { activeItem } from "../nav";
import { topicForPath } from "../help";

export function Header({ pathname, onOpenNav }: { pathname: string; onOpenNav: () => void }) {
  const { t, locale } = useT();
  const { config, me, auth, can } = useConsole();
  const { section, item } = activeItem(pathname);
  const inHelp = pathname === "/help" || pathname.startsWith("/help/");
  const title = inHelp ? t("help.title") : t(section ? section.label : item.label);
  const tabs = inHelp ? [] : (section?.items.filter((i) => can(i.permission)) ?? []);
  // Help icon → the in-console user manual page for this screen. Same tab (client-side navigation, Back returns):
  // the console's session lives in sessionStorage, which a new tab does not share — it would ask to sign in again.
  const helpTopicId = topicForPath(pathname);

  // primary-dark (#006db3) instead of Paperbase #009be5: white text needs ≥ 4.5:1 (WCAG AA).
  return (
    <header className="bg-primary-dark text-white">
      {/* Row 1: actions */}
      <div className="flex h-12 items-center gap-2 px-4 sm:px-6">
        <IconButton label={t("nav.openMenu")} tone="inverse" className="lg:hidden" onClick={onOpenNav}>
          <MenuIcon className="size-5" aria-hidden />
        </IconButton>
        <div className="flex-1" />
        <a
          href={`${config.authServerUrl}/api/v1/docs`}
          target="_blank"
          rel="noreferrer"
          className="rounded-md px-2.5 py-1.5 text-sm font-medium text-white/90 transition-colors hover:bg-white/10 hover:text-white"
        >
          {t("header.docs")}
        </a>
        <Menu.Root>
          <Menu.Trigger asChild>
            <button
              type="button"
              aria-label={t("header.userMenu")}
              className="ml-1 inline-flex size-9 items-center justify-center rounded-full bg-black/20 text-sm font-semibold ring-1 ring-white/25 transition-colors hover:bg-black/30"
            >
              {me.user.avatarUrl ? (
                <img src={me.user.avatarUrl} alt="" className="size-9 rounded-full object-cover" />
              ) : (
                initials(me.user.displayName)
              )}
            </button>
          </Menu.Trigger>
          <Menu.Content>
            <div className="px-4 py-3">
              <p className="text-sm font-medium">{me.user.displayName}</p>
              <p className="text-xs text-ink-2">{me.user.email}</p>
            </div>
            <Menu.Separator />
            <Menu.Label>{t("header.language")}</Menu.Label>
            <Menu.Item onSelect={() => setLocale("th")}>{locale === "th" ? "✓ " : ""}ไทย</Menu.Item>
            <Menu.Item onSelect={() => setLocale("en")}>{locale === "en" ? "✓ " : ""}English</Menu.Item>
            <Menu.Separator />
            <Menu.Item onSelect={() => window.open(`${config.authServerUrl}/account`, "_blank", "noopener")}>
              {t("header.account")}
            </Menu.Item>
            <Menu.Item onSelect={() => void auth.signOut()}>{t("header.signOut")}</Menu.Item>
          </Menu.Content>
        </Menu.Root>
      </div>
      {/* Row 2: title */}
      <div className="flex items-center gap-2 px-4 pt-2 pb-3 sm:px-6">
        <h1 className="flex-1 truncate text-2xl font-semibold tracking-tight">{title}</h1>
        {!inHelp && (
          <Tooltip content={t("header.help")}>
            <Link
              to="/help/$topic"
              params={{ topic: helpTopicId }}
              data-testid="header-help"
              aria-label={t("header.help")}
              className="inline-flex size-9 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/10 hover:text-white"
            >
              <CircleHelp className="size-5" aria-hidden />
            </Link>
          </Tooltip>
        )}
      </div>
      {/* Row 3: tabs (sub-routes) */}
      {tabs.length > 0 && (
        <nav aria-label={t("header.sections")} className="flex gap-2 overflow-x-auto px-4 sm:px-6">
          {tabs.map((tab) => {
            const active = tab.id === item.id;
            return (
              <Link
                key={tab.id}
                to={tab.to}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative shrink-0 rounded-t-md px-3 pt-2 pb-3 text-sm font-medium transition-colors",
                  active
                    ? "text-white after:absolute after:inset-x-2 after:bottom-0 after:h-[3px] after:rounded-t-full after:bg-white"
                    : "text-white/90 hover:bg-white/10 hover:text-white",
                )}
              >
                {t(tab.label)}
              </Link>
            );
          })}
        </nav>
      )}
    </header>
  );
}
